import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import PageLoader from '../../components/PageLoader';
import TablePagination from '../../components/TablePagination';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiTrash2, FiServer, FiEdit, FiRepeat, FiPlus, FiX, FiCheck } from 'react-icons/fi';
import { admin, statusBadgeClass } from '../../components/admin/adminUi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PAGE_SIZE = 15;
const token = () => localStorage.getItem('token');
const authH = () => ({ headers: { Authorization: `Bearer ${token()}` } });

const AdminServers = () => {
  const [servers, setServers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [modal, setModal] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [transferId, setTransferId] = useState(null);
  const [transferUserId, setTransferUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Add Server flow
  const [apiServers, setApiServers] = useState([]);
  const [loadingApi, setLoadingApi] = useState(false);
  const [selectedApiServer, setSelectedApiServer] = useState(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [apiSearch, setApiSearch] = useState('');

  useEffect(() => { fetchServers(); fetchUsers(); }, []);

  const fetchServers = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/servers`, authH());
      setServers(res.data.servers || []);
    } catch { toast.error('Failed to fetch servers'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/users`, authH());
      setUsers(res.data.users || []);
    } catch { /* silent */ }
  };

  const fetchApiServers = async () => {
    setLoadingApi(true);
    try {
      const res = await axios.get(`${API_URL}/admin/acerdp/servers`, authH());
      setApiServers(res.data.servers || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to fetch API servers');
    } finally { setLoadingApi(false); }
  };

  const totalPages = Math.max(1, Math.ceil(servers.length / PAGE_SIZE));
  useEffect(() => { setPage(p => p > totalPages ? 1 : p); }, [servers.length, totalPages]);
  const pagedServers = useMemo(() => servers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [servers, page]);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return users;
    const q = userSearch.toLowerCase();
    return users.filter(u => u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  }, [users, userSearch]);

  const assignedAcerdpIds = useMemo(() => new Set(servers.map(s => s.acerdpServerId).filter(Boolean)), [servers]);

  const filteredApiServers = useMemo(() => {
    let list = apiServers;
    if (apiSearch.trim()) {
      const q = apiSearch.toLowerCase();
      list = list.filter(s => {
        const name = (s.name || s.hostname || s.id || '').toString().toLowerCase();
        const ip = (s.credentials?.ip || s.ip || '').toString().toLowerCase();
        return name.includes(q) || ip.includes(q);
      });
    }
    return list;
  }, [apiServers, apiSearch]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this server?')) return;
    try {
      await axios.delete(`${API_URL}/admin/servers/${id}`, authH());
      toast.success('Server deleted');
      fetchServers();
    } catch { toast.error('Failed to delete server'); }
  };

  const openAdd = () => {
    setSelectedApiServer(null);
    setAssignUserId('');
    setUserSearch('');
    setApiSearch('');
    setModal('add');
    fetchApiServers();
  };

  const getServerFields = (s) => {
    const id = s.id || s.server_id || '';
    const name = s.name || s.hostname || `Server ${id}`;
    const plan = s.package?.name || s.plan || 'Custom';
    const loc = s.location || '';
    const osObj = s.operating_system || s.os || {};
    const os = typeof osObj === 'object' ? (osObj.name || 'Windows Server') : String(osObj);
    const creds = s.credentials || {};
    const ip = creds.ip || s.ip || '';
    const username = creds.username || s.username || '';
    const password = creds.password || s.password || '';
    const pricing = s.package?.pricing || {};
    const price = pricing[loc] || pricing.us || pricing.nl || s.price || 0;
    const exp = s.valid_until || s.expires_at || '';
    const status = s.status === 'online' ? 'active' : (s.status || 'active');
    const rawSpecs = s.package?.specs || {};
    const specs = {
      cores: rawSpecs.cpu_cores || rawSpecs.cores || undefined,
      ram: rawSpecs.ram_gb || rawSpecs.ram || undefined,
      storage: rawSpecs.disk_gb || rawSpecs.storage || undefined,
    };
    return {
      acerdpServerId: String(id), name, plan, location: loc || 'us', os,
      ip, username, password, monthlyPrice: price,
      expiresAt: exp ? new Date(exp.replace(' ', 'T')).toISOString().slice(0, 10) : '',
      status, specs,
    };
  };

  const handleAssign = async () => {
    if (!selectedApiServer) { toast.error('Select a server'); return; }
    if (!assignUserId) { toast.error('Select a user'); return; }
    setSaving(true);
    try {
      const fields = getServerFields(selectedApiServer);
      await axios.post(`${API_URL}/admin/servers`, { ...fields, userId: assignUserId }, authH());
      toast.success('Server assigned successfully');
      closeModal();
      fetchServers();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to assign server');
      setSaving(false);
    }
  };

  const openEdit = (s) => {
    setEditForm({
      name: s.name || '', plan: s.plan || '', location: s.location || '',
      os: s.os || '', ip: s.ip || '', username: s.username || '', password: s.password || '',
      expiresAt: s.expiresAt ? new Date(s.expiresAt).toISOString().slice(0, 10) : '',
      monthlyPrice: s.monthlyPrice ?? '', status: s.status || 'active',
    });
    setEditId(s._id);
    setModal('edit');
  };

  const openTransfer = (s) => {
    setTransferId(s._id);
    setTransferUserId('');
    setUserSearch('');
    setModal('transfer');
  };

  const closeModal = () => { setModal(null); setEditId(null); setTransferId(null); setSaving(false); setSelectedApiServer(null); };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/servers/${editId}`, editForm, authH());
      toast.success('Server updated');
      closeModal();
      fetchServers();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferUserId) { toast.error('Select a user'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/servers/${transferId}/transfer`, { newUserId: transferUserId }, authH());
      toast.success('Server transferred');
      closeModal();
      fetchServers();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Transfer failed');
      setSaving(false);
    }
  };

  if (loading) return <Layout><PageLoader /></Layout>;

  return (
    <Layout>
      <div className={`${admin.pageStretch} min-h-0`}>
        <div className="shrink-0 flex items-center justify-between gap-3">
          <div>
            <h1 className={admin.title}>Servers</h1>
            <p className={admin.subtitle}>{servers.length} server{servers.length !== 1 ? 's' : ''} across all customers.</p>
          </div>
          <button type="button" onClick={openAdd} className={admin.btnPrimary}>
            <FiPlus size={16} /> Add Server
          </button>
        </div>

        <div className={`${admin.tableShell} min-h-0 flex-1`}>
          <div className={admin.tableScroll}>
            <table className={admin.table}>
              <thead className={admin.thead}>
                <tr>
                  <th className={admin.th}>Server</th>
                  <th className={admin.th}>User</th>
                  <th className={admin.th}>Plan</th>
                  <th className={admin.th}>Location</th>
                  <th className={admin.th}>Status</th>
                  <th className={admin.th}>Expires</th>
                  <th className={admin.th}>Actions</th>
                </tr>
              </thead>
              <tbody className={admin.tbody}>
                {pagedServers.map((server) => (
                  <tr key={server._id} className={admin.tr}>
                    <td className={admin.td}>
                      <div className="flex items-center gap-2">
                        <FiServer className="shrink-0 text-[#8BA3FF]" size={18} />
                        <span className="font-medium text-white">{server.name}</span>
                      </div>
                    </td>
                    <td className={`${admin.td} ${admin.tdMuted}`}>{server.userId?.username || 'N/A'}</td>
                    <td className={admin.td}>{server.plan}</td>
                    <td className={`${admin.td} ${admin.tdMuted}`}>{server.location}</td>
                    <td className={admin.td}><span className={statusBadgeClass(server.status)}>{server.status}</span></td>
                    <td className={`${admin.td} ${admin.tdMuted}`}>{new Date(server.expiresAt).toLocaleDateString()}</td>
                    <td className={admin.td}>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => openEdit(server)} className={admin.btnIcon} title="Edit"><FiEdit size={18} /></button>
                        <button type="button" onClick={() => openTransfer(server)} className={admin.btnIcon} title="Transfer"><FiRepeat size={18} /></button>
                        <button type="button" onClick={() => handleDelete(server._id)} className={admin.btnDanger} title="Delete"><FiTrash2 size={18} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination page={page} pageSize={PAGE_SIZE} total={servers.length} onPageChange={setPage} />
        </div>
      </div>

      {/* Add Server Modal — pick from API + assign to user */}
      {modal === 'add' && (
        <div className={admin.modalOverlay} onMouseDown={closeModal}>
          <div className="w-full max-w-lg rounded-2xl border border-[#B8B8B847] bg-[#0f1428]/95 backdrop-blur-md p-6 shadow-[0_24px_50px_rgba(0,0,0,0.55)] max-h-[90vh] flex flex-col" onMouseDown={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5 shrink-0">
              <h2 className={admin.modalTitle + ' mb-0'}>Add Server</h2>
              <button type="button" onClick={closeModal} className="text-white/50 hover:text-white"><FiX size={20} /></button>
            </div>

            {/* Step 1: Pick server from API */}
            <div className="shrink-0 mb-4">
              <label className={admin.label}>1. Select server from API</label>
              <input placeholder="Search by name or IP..." value={apiSearch} onChange={e => setApiSearch(e.target.value)} className={admin.input} />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto mb-4 -mr-2 pr-2">
              {loadingApi ? (
                <div className="flex items-center justify-center py-10"><div className="h-7 w-7 animate-spin rounded-full border-2 border-white/20 border-t-[#7B9CFF]" /></div>
              ) : filteredApiServers.length === 0 ? (
                <div className="text-center py-10 text-white/40">No servers found from API.</div>
              ) : (
                <div className="space-y-2">
                  {filteredApiServers.map((s) => {
                    const id = String(s.id || s.server_id || '');
                    const name = s.name || s.hostname || `Server ${id}`;
                    const creds = s.credentials || {};
                    const ip = creds.ip || s.ip || '';
                    const plan = s.package?.name || s.plan || '';
                    const status = s.status || '';
                    const osObj = s.operating_system || s.os || {};
                    const osLabel = typeof osObj === 'object' ? (osObj.name || '') : String(osObj);
                    const alreadyAssigned = assignedAcerdpIds.has(id);
                    const isSelected = selectedApiServer && String(selectedApiServer.id || selectedApiServer.server_id || selectedApiServer._id) === id;

                    return (
                      <button key={id} type="button" disabled={alreadyAssigned}
                        onClick={() => setSelectedApiServer(s)}
                        className={`w-full text-left rounded-xl border p-3.5 transition ${alreadyAssigned
                          ? 'border-white/5 bg-white/[0.02] opacity-40 cursor-not-allowed'
                          : isSelected
                            ? 'border-[#3C3CFF] bg-[#3C3CFF]/15 ring-1 ring-[#3C3CFF]/50'
                            : 'border-[#FFFFFF14] bg-[#FFFFFF06] hover:border-[#3C3CFF]/50 hover:bg-[#3C3CFF]/10 cursor-pointer'}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <FiServer className="shrink-0 text-[#8BA3FF]" size={15} />
                              <span className="font-medium text-white truncate">{name}</span>
                              {alreadyAssigned && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-white/10 border-white/15 text-white/50">Assigned</span>}
                            </div>
                            <div className="text-xs text-white/40 mt-1 flex flex-wrap gap-x-3">
                              {ip && <span>IP: {ip}</span>}
                              {plan && <span>Plan: {plan}</span>}
                              {osLabel && <span>OS: {osLabel}</span>}
                              <span className={statusBadgeClass(status)}>{status}</span>
                            </div>
                          </div>
                          {isSelected && <FiCheck className="text-[#3C3CFF] shrink-0" size={20} />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Step 2: Pick user */}
            <div className="shrink-0 border-t border-white/10 pt-4">
              <label className={admin.label}>2. Assign to user</label>
              <input placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className={admin.input + ' mb-2'} />
              <select value={assignUserId} onChange={e => setAssignUserId(e.target.value)} className={admin.select + ' w-full'}>
                <option value="">-- Select User --</option>
                {filteredUsers.map(u => <option key={u._id} value={u._id}>{u.username} ({u.email})</option>)}
              </select>

              <button type="button" disabled={saving || !selectedApiServer || !assignUserId} onClick={handleAssign} className={`${admin.btnPrimary} mt-4 w-full`}>
                {saving ? 'Assigning...' : 'Assign Server'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {modal === 'edit' && (
        <div className={admin.modalOverlay} onMouseDown={closeModal}>
          <div className="w-full max-w-lg rounded-2xl border border-[#B8B8B847] bg-[#0f1428]/95 backdrop-blur-md p-6 shadow-[0_24px_50px_rgba(0,0,0,0.55)] max-h-[90vh] overflow-y-auto" onMouseDown={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className={admin.modalTitle + ' mb-0'}>Edit Server</h2>
              <button type="button" onClick={closeModal} className="text-white/50 hover:text-white"><FiX size={20} /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={admin.label}>Name</label>
                  <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className={admin.input} />
                </div>
                <div>
                  <label className={admin.label}>Plan</label>
                  <input value={editForm.plan} onChange={e => setEditForm(f => ({ ...f, plan: e.target.value }))} className={admin.input} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={admin.label}>Location</label>
                  <select value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} className={admin.select + ' w-full'}>
                    <option value="us">United States</option>
                    <option value="nl">Netherlands</option>
                  </select>
                </div>
                <div>
                  <label className={admin.label}>OS</label>
                  <input value={editForm.os} onChange={e => setEditForm(f => ({ ...f, os: e.target.value }))} className={admin.input} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={admin.label}>IP</label>
                  <input value={editForm.ip} onChange={e => setEditForm(f => ({ ...f, ip: e.target.value }))} className={admin.input} />
                </div>
                <div>
                  <label className={admin.label}>Username</label>
                  <input value={editForm.username} onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))} className={admin.input} />
                </div>
                <div>
                  <label className={admin.label}>Password</label>
                  <input value={editForm.password} onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} className={admin.input} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={admin.label}>Expires</label>
                  <input type="date" value={editForm.expiresAt} onChange={e => setEditForm(f => ({ ...f, expiresAt: e.target.value }))} className={admin.input} />
                </div>
                <div>
                  <label className={admin.label}>Price/mo</label>
                  <input type="number" step="0.01" value={editForm.monthlyPrice} onChange={e => setEditForm(f => ({ ...f, monthlyPrice: e.target.value }))} className={admin.input} />
                </div>
                <div>
                  <label className={admin.label}>Status</label>
                  <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} className={admin.select + ' w-full'}>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>
            </div>
            <button type="button" disabled={saving} onClick={handleEditSave} className={`${admin.btnPrimary} mt-6 w-full`}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {modal === 'transfer' && (
        <div className={admin.modalOverlay} onMouseDown={closeModal}>
          <div className={admin.modalCard} onMouseDown={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className={admin.modalTitle + ' mb-0'}>Transfer Server</h2>
              <button type="button" onClick={closeModal} className="text-white/50 hover:text-white"><FiX size={20} /></button>
            </div>
            <label className={admin.label}>Transfer to User</label>
            <input placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className={admin.input + ' mb-2'} />
            <select value={transferUserId} onChange={e => setTransferUserId(e.target.value)} className={admin.select + ' w-full'}>
              <option value="">-- Select User --</option>
              {filteredUsers.map(u => <option key={u._id} value={u._id}>{u.username} ({u.email})</option>)}
            </select>
            <button type="button" disabled={saving} onClick={handleTransfer} className={`${admin.btnPrimary} mt-6 w-full`}>
              {saving ? 'Transferring...' : 'Transfer Server'}
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminServers;
